import type { DiagramNode, DiagramPage } from './types';

const FLOW_TYPES = new Set(['bpmnTask', 'bpmnSubprocess', 'bpmnEvent', 'bpmnBoundaryEvent', 'bpmnGateway']);

function containsCenter(container: DiagramNode, node: DiagramNode): boolean {
  const cx = node.x + node.width / 2, cy = node.y + node.height / 2;
  return cx >= container.x && cx <= container.x + container.width && cy >= container.y && cy <= container.y + container.height;
}

export interface BpmnMembership {
  laneToPool: Map<string, string>;
  nodeToLane: Map<string, string>;
  nodeToPool: Map<string, string>;
}

export function inferBpmnMembership(page: DiagramPage, options: { preferExplicit?: boolean } = {}): BpmnMembership {
  const preferExplicit = options.preferExplicit ?? true;
  const pools = page.nodes.filter((node) => node.type === 'bpmnPool' && !node.hidden);
  const lanes = page.nodes.filter((node) => node.type === 'bpmnLane' && !node.hidden);
  const flows = page.nodes.filter((node) => FLOW_TYPES.has(node.type) && !node.hidden);
  const laneToPool = new Map<string, string>();
  const nodeToLane = new Map<string, string>();
  const nodeToPool = new Map<string, string>();

  for (const lane of lanes) {
    const explicit = preferExplicit && typeof lane.data.poolId === 'string' && pools.some((pool) => pool.id === lane.data.poolId) ? String(lane.data.poolId) : null;
    const pool = explicit ? pools.find((item) => item.id === explicit) : pools.filter((item) => containsCenter(item, lane)).sort((a, b) => a.width * a.height - b.width * b.height)[0];
    if (pool) laneToPool.set(lane.id, pool.id);
  }
  for (const node of flows) {
    const explicitLane = preferExplicit && typeof node.data.laneId === 'string' && lanes.some((lane) => lane.id === node.data.laneId) ? String(node.data.laneId) : null;
    const lane = explicitLane ? lanes.find((item) => item.id === explicitLane) : lanes.filter((item) => containsCenter(item, node)).sort((a, b) => a.width * a.height - b.width * b.height)[0];
    if (lane) {
      nodeToLane.set(node.id, lane.id);
      const poolId = laneToPool.get(lane.id); if (poolId) nodeToPool.set(node.id, poolId);
    } else {
      const explicitPool = preferExplicit && typeof node.data.poolId === 'string' && pools.some((pool) => pool.id === node.data.poolId) ? String(node.data.poolId) : null;
      const pool = explicitPool ? pools.find((item) => item.id === explicitPool) : pools.filter((item) => containsCenter(item, node)).sort((a, b) => a.width * a.height - b.width * b.height)[0];
      if (pool) nodeToPool.set(node.id, pool.id);
    }
  }
  return { laneToPool, nodeToLane, nodeToPool };
}

export function normalizeBpmnMembership(page: DiagramPage): DiagramPage {
  const membership = inferBpmnMembership(page, { preferExplicit: false });
  return {
    ...page,
    nodes: page.nodes.map((node) => {
      if (node.type === 'bpmnLane') {
        const poolId = membership.laneToPool.get(node.id) ?? null;
        return { ...node, data: { ...node.data, poolId } };
      }
      if (FLOW_TYPES.has(node.type)) {
        const laneId = membership.nodeToLane.get(node.id) ?? null;
        const poolId = membership.nodeToPool.get(node.id) ?? null;
        return { ...node, data: { ...node.data, laneId, poolId } };
      }
      return node;
    }),
  };
}
